import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { ArrowLeft, Search, FileText, Building2, Chrome, Shield, Smartphone, Bot, HardDrive, Monitor, CheckCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import Breadcrumb from './Breadcrumb';
import { searchInContent } from '../mock';
import Footer from './Footer';

const SearchResultsPage = () => {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [searchQuery, setSearchQuery] = useState(query);
  const [searchResults, setSearchResults] = useState([]);

  useEffect(() => {
    if (query) {
      performSearch(query);
    }
  }, [query]);

  const performSearch = (searchTerm) => {
    if (searchTerm.trim()) {
      // Search across all categories
      const results = searchInContent(searchTerm);
      setSearchResults(results);
    } else {
      setSearchResults([]);
    }
  };

  const handleNewSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      window.history.pushState({}, '', `/search?q=${encodeURIComponent(searchQuery)}`);
      performSearch(searchQuery);
    }
  };

  const getCategoryInfo = (category) => {
    const categoryMap = {
      'office365': { name: 'Office 365', icon: Building2, color: 'blue' },
      'google-workspace': { name: 'Google Workspace', icon: Chrome, color: 'green' },
      'security': { name: 'Sécurité', icon: Shield, color: 'red' },
      'mobile': { name: 'Appareils Mobiles', icon: Smartphone, color: 'purple' },
      'intelligence-artificielle': { name: 'Intelligence Artificielle', icon: Bot, color: 'indigo' },
      'synology': { name: 'Synology', icon: HardDrive, color: 'teal' },
      'operating-systems': { name: 'Systèmes OS', icon: Monitor, color: 'slate' }
    };
    return categoryMap[category] || { name: category, icon: FileText, color: 'gray' };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {/* Header Search */}
      <section className="relative overflow-hidden bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-white bg-opacity-20 rounded-3xl mb-8 backdrop-blur-sm">
              <Search className="h-12 w-12 text-white" />
            </div>
            <h1 className="text-5xl md:text-6xl font-extrabold text-white mb-4">
              Résultats de recherche
            </h1>
            <p className="text-indigo-100 text-xl md:text-2xl max-w-4xl leading-relaxed">
              {query ? `Résultats pour "${query}"` : 'Recherchez dans nos guides et actualités'}
            </p>
          </div>
        </div>
        
        {/* Floating elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-yellow-300 rounded-full opacity-20 animate-bounce"></div>
        <div className="absolute bottom-20 right-10 w-16 h-16 bg-pink-300 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute top-1/2 left-1/4 w-12 h-12 bg-cyan-300 rounded-full opacity-25 animate-ping"></div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Breadcrumb */}
        <Breadcrumb 
          items={[
            { id: 'search', label: 'Résultats de recherche' }
          ]}
        />

        {/* Search Bar */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6 mb-8">
          <form onSubmit={handleNewSearch} className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Affinez votre recherche..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 py-3 text-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg"
              />
            </div>
            <Button 
              type="submit"
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold px-6 py-3 rounded-lg"
            >
              Rechercher
            </Button>
          </form>
          
          {searchResults.length > 0 && (
            <div className="mt-4 text-sm text-gray-600">
              {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} trouvé{searchResults.length > 1 ? 's' : ''} pour "{query}"
            </div>
          )}
        </div>

        {/* Search Results */}
        {searchResults.length > 0 ? (
          <div className="space-y-4">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <FileText className="h-6 w-6 mr-3 text-indigo-600" />
              Guides et procédures trouvés
            </h2>
            
            {searchResults.map((result, index) => {
              const categoryInfo = getCategoryInfo(result.category);
              const CategoryIcon = categoryInfo.icon;
              
              return (
                <Card key={index} className="group cursor-pointer transition-all duration-200 hover:shadow-lg border border-gray-200 overflow-hidden bg-white">
                  <div className="p-6">
                    <div className="flex items-start gap-4">
                      {/* Category Icon */}
                      <div className={`p-3 rounded-xl bg-gradient-to-br ${
                        categoryInfo.color === 'blue' ? 'from-blue-500 to-blue-600' :
                        categoryInfo.color === 'green' ? 'from-green-500 to-green-600' :
                        categoryInfo.color === 'red' ? 'from-red-500 to-red-600' :
                        categoryInfo.color === 'purple' ? 'from-purple-500 to-purple-600' :
                        categoryInfo.color === 'indigo' ? 'from-indigo-500 to-indigo-600' :
                        categoryInfo.color === 'teal' ? 'from-teal-500 to-teal-600' :
                        categoryInfo.color === 'slate' ? 'from-slate-500 to-slate-600' :
                        'from-gray-500 to-gray-600'
                      } shadow-lg flex-shrink-0 group-hover:scale-110 transition-transform duration-300`}>
                        <CategoryIcon className="h-6 w-6 text-white" />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-grow">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <Badge className={`${
                              categoryInfo.color === 'blue' ? 'bg-blue-100 text-blue-800' :
                              categoryInfo.color === 'green' ? 'bg-green-100 text-green-800' :
                              categoryInfo.color === 'red' ? 'bg-red-100 text-red-800' :
                              categoryInfo.color === 'purple' ? 'bg-purple-100 text-purple-800' :
                              categoryInfo.color === 'indigo' ? 'bg-indigo-100 text-indigo-800' :
                              categoryInfo.color === 'teal' ? 'bg-teal-100 text-teal-800' :
                              categoryInfo.color === 'slate' ? 'bg-slate-100 text-slate-800' :
                              'bg-gray-100 text-gray-800'
                            } font-medium px-3 py-1 text-sm`}>
                              {categoryInfo.name}
                            </Badge>
                            <Badge className={`${
                              result.isSubPage ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'
                            } font-medium px-3 py-1 text-sm`}>
                              {result.isSubPage ? 'Procédure' : 'Guide'}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center text-gray-500 text-sm">
                            {result.estimatedTime && (
                              <span className="flex items-center">
                                <Clock className="h-4 w-4 mr-1" />
                                {result.estimatedTime}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <h3 className="text-xl font-semibold text-gray-900 mb-3 group-hover:text-indigo-600 transition-colors duration-200">
                          {result.title}
                        </h3>
                        
                        <p className="text-gray-600 leading-relaxed mb-4">
                          {result.description}
                        </p>
                        
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-2">
                            {result.tags && result.tags.slice(0, 3).map((tag, tagIndex) => (
                              <span key={tagIndex} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                                {tag}
                              </span>
                            ))}
                          </div>
                          
                          <Link 
                            to={`/${result.category}/guides/${result.id}`}
                            className={`bg-gradient-to-r ${
                              categoryInfo.color === 'blue' ? 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700' :
                              categoryInfo.color === 'green' ? 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700' :
                              categoryInfo.color === 'red' ? 'from-red-500 to-red-600 hover:from-red-600 hover:to-red-700' :
                              categoryInfo.color === 'purple' ? 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700' :
                              categoryInfo.color === 'indigo' ? 'from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700' :
                              categoryInfo.color === 'teal' ? 'from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700' :
                              categoryInfo.color === 'slate' ? 'from-slate-500 to-slate-600 hover:from-slate-600 hover:to-slate-700' :
                              'from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
                            } text-white font-semibold px-4 py-2 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-sm inline-flex items-center`}
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            {result.isSubPage ? 'Voir la procédure' : 'Voir le guide'}
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        ) : query ? (
          /* No Results */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-8">
              <Search className="h-10 w-10 text-gray-400" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Aucun résultat trouvé</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Aucun guide ne correspond à votre recherche "{query}". Essayez d'autres mots-clés ou explorez nos catégories.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/office365" className="bg-blue-100 text-blue-800 px-4 py-2 rounded-lg font-medium hover:bg-blue-200 transition-colors">
                Office 365
              </Link>
              <Link to="/google-workspace" className="bg-green-100 text-green-800 px-4 py-2 rounded-lg font-medium hover:bg-green-200 transition-colors">
                Google Workspace
              </Link>
              <Link to="/security" className="bg-red-100 text-red-800 px-4 py-2 rounded-lg font-medium hover:bg-red-200 transition-colors">
                Sécurité
              </Link>
              <Link to="/ia/conversational" className="bg-indigo-100 text-indigo-800 px-4 py-2 rounded-lg font-medium hover:bg-indigo-200 transition-colors">
                Intelligence IA
              </Link>
            </div>
          </div>
        ) : (
          /* No Search Query */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-indigo-100 rounded-full mb-8">
              <Search className="h-10 w-10 text-indigo-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Recherchez dans nos guides</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Utilisez la barre de recherche ci-dessus pour trouver des guides, procédures et actualités.
            </p>
          </div>
        )}

        {/* Back to Home */}
        <div className="text-center mt-12">
          <Link to="/">
            <Button className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-bold px-8 py-4 rounded-full text-lg shadow-lg transform hover:scale-105 transition-all duration-300">
              <ArrowLeft className="h-5 w-5 mr-2" />
              Retour à l'accueil
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SearchResultsPage;